/**
 * folderScan — turn a dropped/picked folder tree into validated expedientes.
 *
 * Convention (carga masiva): the operator drops ONE folder whose subfolders are
 * each an expediente named by the applicant's CURP, holding documents named
 * either `{CURP}_{TIPO}` (`ANVERIDEN`/`IDEN` for the identification, `CERT`,
 * `FOTO`) or by the legacy canonical names (`ine.jpg`, `certificado.pdf`,
 * `foto.jpg`, optional `ine_reverso.*`). Identification files declare no side
 * (ADR 0007): a folder with a single identification file fills BOTH ine slots
 * with it. We group every file by its IMMEDIATE parent directory, so
 * wrapper folders, a multi-folder drop, and stray loose files all resolve
 * uniformly: a directory that directly holds documents is an expediente
 * candidate; anything else surfaces as a problem row, never silently.
 *
 * Validation mirrors the server intake gate (docs/architecture.md) so a folder
 * that passes here will not be structurally rejected by the engine: required
 * kinds present, accepted extensions, a magic-byte signature matching the
 * file's format FAMILY — JPEG and PNG are interchangeable (sniffed from real
 * content, so a real JPEG named `foto.png` is accepted, not flagged corrupt),
 * but PDF stays strictly tied to the `.pdf` extension (the engine routes
 * PDF-vs-image purely by extension downstream — see `sniffContentType`) — the
 * 20 MB cap (Thresholds.max_file_size_mb in src/vera/config.py), and
 * duplicate-kind detection (INTAKE_DUPLICATE_DOCUMENT is a blocking failure
 * server-side).
 */

import { DOC_KINDS, REQUIRED_DOC_KINDS, DOC_KIND_LABEL, type DocKind } from "./uploadTypes";

/** One file discovered in the dropped tree, with its path relative to the drop. */
export type ScannedFile = {
  /** e.g. "carpeta/GABA050728HDFRRN09/ine.jpg" — always "/" separated. */
  relPath: string;
  file: File;
};

/** A document ready to sign+PUT: declared tipo (kind) + exact content type. */
export type BulkFile = {
  /** The declared document `tipo` sent to signUploads (validated against the profile). */
  kind: DocKind;
  file: File;
  /** Canonical display name (e.g. "ine.jpg") for operator-facing messages. */
  name: string;
  /** Content type sent to sign AND the PUT (signed URLs bind this header). */
  contentType: string;
};

export type FolderProblem = {
  code:
    | "CURP_INVALIDA"
    | "CURP_DUPLICADA"
    | "FALTA_DOCUMENTO"
    | "DOCUMENTO_DUPLICADO"
    | "ARCHIVO_GRANDE"
    | "ARCHIVO_CORRUPTO"
    | "CARPETA_VACIA";
  message: string;
};

export type ScannedExpediente = {
  /** Folder name as dropped (last path segment of the parent dir). */
  folderName: string;
  /** Full parent path — unique group key even if two folders share a name. */
  parentPath: string;
  /** Normalized id used as expediente_id: trimmed + uppercased folder name. */
  expedienteId: string;
  files: BulkFile[];
  /** Files in the folder we will NOT upload (unrecognized names), verbatim. */
  ignored: string[];
  /** Empty ⇒ the folder is uploadable. */
  problems: FolderProblem[];
  totalBytes: number;
};

export type ScanResult = {
  ready: ScannedExpediente[];
  problematic: ScannedExpediente[];
  /** Files sitting at the root of the drop, outside any folder (informative). */
  looseRootFiles: string[];
  /** Junk entries skipped silently (.DS_Store, __MACOSX, …). */
  junkSkipped: number;
  totalReadyFiles: number;
  totalReadyBytes: number;
};

/** Hard cap: a wrong drop (e.g. the whole home folder) aborts with a clear error. */
export const MAX_SCAN_FILES = 60_000;
/** Mirror of Thresholds.max_file_size_mb (src/vera/config.py). */
export const MAX_FILE_MB = 20;

export class ScanLimitError extends Error {
  constructor(count: number) {
    super(
      `La carpeta contiene más de ${MAX_SCAN_FILES.toLocaleString("es-MX")} archivos ` +
        `(${count.toLocaleString("es-MX")} encontrados). Verifica que sea la carpeta correcta.`,
    );
    this.name = "ScanLimitError";
  }
}

/* ── CURP ──────────────────────────────────────────────────────────────────── */

/** Full CURP shape: initials, birthdate, sex, state code (incl. NE), consonants,
 * homoclave (digit pre-2000 / letter after), check digit. Client-side mirror of
 * the EXACER profile's expediente-id rule (server: PROFILE_ID_VIOLATION) — the
 * contract itself treats expediente_id as opaque. */
const CURP_RE =
  /^[A-Z][AEIOUX][A-Z]{2}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[HM](AS|BC|BS|CC|CL|CM|CS|CH|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SP|SL|SR|TC|TS|TL|VZ|YN|ZS|NE)[B-DF-HJ-NP-TV-Z]{3}[A-Z0-9]\d$/;

export function isValidCurp(value: string): boolean {
  return CURP_RE.test(value);
}

/* ── Classification (mirrors identify_document_type in capa0_ingesta.py) ───── */

const ACCEPTED_EXT_CONTENT_TYPE: Record<string, string> = {
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".pdf": "application/pdf",
};

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot).toLowerCase() : "";
}

/** Token → kind vocabulary — a verbatim mirror of `_TOKEN_VOCABULARY` in
 * `src/vera/pipeline/capa0_ingesta.py` (ADR 0007): one global table shared by
 * all tenants, matched EXACTLY per token, unknown tokens ignored. Keep both
 * copies in lockstep. */
const TOKEN_VOCABULARY: Record<string, DocKind> = {
  certificado: "certificado",
  cert: "certificado",
  secundaria: "certificado",
  // ANVERIDEN / IDEN name the identification WITHOUT declaring a side (ADR
  // 0007): ANVERIDEN fills the front slot, IDEN the back slot, optimistically —
  // the extractor adjudicates what each file actually shows.
  iden: "ine_reverso",
  reverso: "ine_reverso",
  back: "ine_reverso",
  trasero: "ine_reverso",
  vuelta: "ine_reverso",
  anveriden: "ine",
  ine: "ine",
  credencial: "ine",
  ife: "ine",
  identificacion: "ine",
  foto: "foto",
  photo: "foto",
  selfie: "foto",
  reciente: "foto",
  recent: "foto",
};

/** Tie-break when a name carries tokens of several kinds — same order as the
 * server (`_KIND_PRECEDENCE`): certificado → reverso → ine → foto. */
const KIND_PRECEDENCE: DocKind[] = ["certificado", "ine_reverso", "ine", "foto"];

/**
 * Classify a file by exact filename tokens: the stem (extension stripped) is
 * split on `_`, whitespace and `-`, each token compared exactly against the
 * global vocabulary, ties broken by KIND_PRECEDENCE. A miss returns null
 * (file ignored). Substring matching is gone: under the {CURP}_{TIPO}
 * convention a CURP head like "PINE…" or "CERT…" is structurally legal and
 * must never steal the match.
 */
export function classifyDocKind(fileName: string): DocKind | null {
  const dot = fileName.lastIndexOf(".");
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  const matched = new Set<DocKind>();
  for (const token of stem.toLowerCase().split(/[_\s-]+/)) {
    // hasOwnProperty guard: a token like "constructor" must not hit the prototype.
    if (Object.prototype.hasOwnProperty.call(TOKEN_VOCABULARY, token)) {
      matched.add(TOKEN_VOCABULARY[token]);
    }
  }
  for (const kind of KIND_PRECEDENCE) {
    if (matched.has(kind)) return kind;
  }
  return null;
}

/* ── Ignored-file messaging (#281) ─────────────────────────────────────────── */

/** How many ignored names to spell out before collapsing into "y N más". */
const IGNORED_LIST_CAP = 5;

/**
 * Operator-facing note for `ScannedExpediente.ignored` — the files a folder
 * holds that will NOT upload (unrecognized name or extension). Under the
 * `{CURP}_{TIPO}` convention a suffix typo lands here, so the names must reach
 * the screen verbatim: in the ensayo #276 (ADR 0007) a `_REVERIDEN` probe was
 * dropped with no trace and the expediente uploaded as "listo". Null when
 * nothing was ignored.
 */
export function ignoredFilesMessage(ignored: string[]): string | null {
  if (ignored.length === 0) return null;
  return ignored.length === 1
    ? `Archivo no reconocido (no se cargará): ${ignoredFilesList(ignored)}.`
    : `Archivos no reconocidos (no se cargarán): ${ignoredFilesList(ignored)}.`;
}

/** The capped name list alone — for table cells whose header carries the
 * "no se cargarán" context. */
export function ignoredFilesList(ignored: string[]): string {
  const shown = ignored.slice(0, IGNORED_LIST_CAP).join(", ");
  const rest = ignored.length - IGNORED_LIST_CAP;
  return rest > 0 ? `${shown} y ${n(rest)} más` : shown;
}

/** es-MX integer formatting, shared with the uploader UI. */
export function n(value: number): string {
  return value.toLocaleString("es-MX");
}

/** Ready folders that hold files we will NOT upload — each deserves a visible
 * warning in the scan summary so a suffix typo never travels silently. */
export function readyExpedientesWithIgnored(ready: ScannedExpediente[]): ScannedExpediente[] {
  return ready.filter((e) => e.ignored.length > 0);
}

/* ── Junk filtering ────────────────────────────────────────────────────────── */

const JUNK_NAMES = new Set([".ds_store", "thumbs.db", "desktop.ini", "icon\r"]);
const JUNK_SEGMENTS = new Set(["__macosx"]);

function isJunkPath(relPath: string): boolean {
  const segments = relPath.split("/");
  const name = segments[segments.length - 1].toLowerCase();
  if (name.startsWith(".") || JUNK_NAMES.has(name)) return true;
  return segments.some((s) => JUNK_SEGMENTS.has(s.toLowerCase()));
}

/* ── Magic bytes (mirrors has_valid_signature in capa0_ingesta.py) ─────────── */

const SIGNATURES: Record<string, number[][]> = {
  "image/jpeg": [[0xff, 0xd8, 0xff]],
  "image/png": [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  "application/pdf": [[0x25, 0x50, 0x44, 0x46]], // %PDF
};

/** Content types treated as one interchangeable "image" family for sniffing. */
const IMAGE_CONTENT_TYPES = ["image/jpeg", "image/png"];

/** Canonical extension for a sniffed content type — mirrors the server's
 * `_UPLOAD_CONTENT_TYPE_EXT` (src/vera/api/main.py), which names the stored
 * object from the (now content-derived) declared content type. */
const CONTENT_TYPE_EXT: Record<string, string> = {
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "application/pdf": ".pdf",
};

/**
 * Detect the file's real format from its leading bytes. Images (JPEG/PNG) are
 * interchangeable — a real JPEG saved as `.png` sniffs as `image/jpeg` and is
 * accepted — but PDF is kept isolated from images: the engine decides
 * PDF-vs-image routing purely from the extension (`rasterize_pdf_documents`,
 * `_image_part` in capa0_ingesta.py / vision_extract.py, with no try/except
 * around a mismatch), so letting PDF bytes pass under an image extension (or
 * vice versa) would slip past this gate and crash deeper in the pipeline
 * instead of failing cleanly here. `declaredContentType` (the
 * extension-derived guess) only scopes which family of signatures is
 * eligible — it is not itself trusted. Returns `null` if the bytes match
 * nothing in the eligible family (or the handle is unreadable).
 */
async function sniffContentType(file: File, declaredContentType: string): Promise<string | null> {
  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  } catch {
    return null; // unreadable file handle (e.g. moved after the drop)
  }
  const candidates =
    declaredContentType === "application/pdf" ? ["application/pdf"] : IMAGE_CONTENT_TYPES;
  for (const contentType of candidates) {
    const sigs = SIGNATURES[contentType];
    if (sigs.some((sig) => bytes.length >= sig.length && sig.every((b, i) => bytes[i] === b))) {
      return contentType;
    }
  }
  return null;
}

/* ── Traversal adapters ────────────────────────────────────────────────────── */

/** Folder picker (`<input webkitdirectory>`) → ScannedFile[]. */
export function filesFromInput(list: FileList): ScannedFile[] {
  const out: ScannedFile[] = [];
  for (let i = 0; i < list.length; i++) {
    const f = list[i];
    // webkitRelativePath is "" for a plain (non-directory) selection.
    out.push({ relPath: f.webkitRelativePath || f.name, file: f });
  }
  return out;
}

/**
 * Drag-and-drop → ScannedFile[]. MUST be called synchronously from the drop
 * handler: `webkitGetAsEntry()` is only valid before the event yields, so we
 * snapshot every entry first and only then walk asynchronously.
 */
export function entriesFromDataTransfer(items: DataTransferItemList): FileSystemEntry[] {
  const entries: FileSystemEntry[] = [];
  for (let i = 0; i < items.length; i++) {
    const entry = items[i].webkitGetAsEntry();
    if (entry) entries.push(entry);
  }
  return entries;
}

/** What a drag-and-drop walk yields: files plus file-less directories. The
 * picker path cannot see empty directories (a FileList only carries files —
 * browser limitation), so only drops report them. */
export type DroppedTree = {
  files: ScannedFile[];
  /** Topmost directories with NO files anywhere beneath (each becomes a
   * problem row — an expediente folder with zero documents must not vanish). */
  emptyDirPaths: string[];
};

export async function filesFromEntries(entries: FileSystemEntry[]): Promise<DroppedTree> {
  const files: ScannedFile[] = [];
  const emptyDirPaths: string[] = [];
  for (const entry of entries) {
    await walkEntry(entry, "", files, emptyDirPaths);
  }
  return { files, emptyDirPaths };
}

const MAX_DEPTH = 8;

/** Returns the number of files found beneath `entry`. */
async function walkEntry(
  entry: FileSystemEntry,
  prefix: string,
  out: ScannedFile[],
  emptyDirs: string[],
): Promise<number> {
  if (out.length > MAX_SCAN_FILES) throw new ScanLimitError(out.length);
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(resolve, reject),
    );
    out.push({ relPath: prefix + entry.name, file });
    return 1;
  }
  if (!entry.isDirectory) return 0;
  if (prefix.split("/").length > MAX_DEPTH) return 0; // cycle/nesting guard
  const dirPath = `${prefix}${entry.name}`;
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  let found = 0;
  // readEntries returns AT MOST ~100 entries per call (Chrome) — loop until empty.
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    );
    if (batch.length === 0) break;
    for (const child of batch) {
      found += await walkEntry(child, `${dirPath}/`, out, emptyDirs);
    }
  }
  if (found === 0) {
    // Report only the TOPMOST empty dir: absorb any empty descendants.
    for (let i = emptyDirs.length - 1; i >= 0; i--) {
      if (emptyDirs[i].startsWith(`${dirPath}/`)) emptyDirs.splice(i, 1);
    }
    emptyDirs.push(dirPath);
  }
  return found;
}

/* ── Analysis ──────────────────────────────────────────────────────────────── */

export type ScanProgress = (checked: number, total: number) => void;

/**
 * Group, classify, and validate. `onProgress` reports the magic-byte pass
 * (the only part that reads file bytes; 8 bytes per document).
 * `emptyDirPaths` (drop path only) become problem rows so a document-less
 * folder never disappears silently.
 */
export async function analyzeScannedFiles(
  scanned: ScannedFile[],
  onProgress?: ScanProgress,
  emptyDirPaths: string[] = [],
): Promise<ScanResult> {
  if (scanned.length > MAX_SCAN_FILES) throw new ScanLimitError(scanned.length);

  const looseRootFiles: string[] = [];
  let junkSkipped = 0;

  // Group by immediate parent directory path.
  const groups = new Map<string, { name: string; files: { name: string; file: File }[] }>();
  for (const { relPath, file } of scanned) {
    if (isJunkPath(relPath)) {
      junkSkipped++;
      continue;
    }
    const segments = relPath.split("/").filter(Boolean);
    if (segments.length < 2) {
      looseRootFiles.push(relPath);
      continue;
    }
    const parentPath = segments.slice(0, -1).join("/");
    const fileName = segments[segments.length - 1];
    let group = groups.get(parentPath);
    if (!group) {
      group = { name: segments[segments.length - 2], files: [] };
      groups.set(parentPath, group);
    }
    group.files.push({ name: fileName, file });
  }

  // Build per-folder results.
  const expedientes: ScannedExpediente[] = [];
  for (const [parentPath, group] of groups) {
    const problems: FolderProblem[] = [];
    const ignored: string[] = [];
    const slots = new Map<DocKind, { file: File; sourceName: string }>();

    // Sorted order mirrors the server's iterdir() duplicate resolution.
    const sorted = [...group.files].sort((a, b) => a.name.localeCompare(b.name));
    const duplicates: string[] = [];
    for (const { name, file } of sorted) {
      const ext = extensionOf(name);
      const kind = ACCEPTED_EXT_CONTENT_TYPE[ext] ? classifyDocKind(name) : null;
      if (!kind) {
        ignored.push(name);
        continue;
      }
      if (slots.has(kind)) {
        duplicates.push(`${name} (${DOC_KIND_LABEL[kind]})`);
        continue;
      }
      slots.set(kind, { file, sourceName: name });
    }

    // ADR 0007 (#274): identification files declare no side. A folder with
    // exactly ONE identification file is assumed to show both sides until the
    // image says otherwise: the same File fills BOTH slots and is uploaded
    // twice (ine.{ext} + ine_reverso.{ext}), so the extractor reads it once
    // labeled FRENTE and once REVERSO and a both-sides sheet can approve. A
    // genuinely one-sided file degrades to PENDING → revisión on its own.
    // Unconditional, PDFs included — the console never counts PDF pages.
    const ineSlot = slots.get("ine");
    const reversoSlot = slots.get("ine_reverso");
    if (ineSlot && !reversoSlot) slots.set("ine_reverso", ineSlot);
    else if (reversoSlot && !ineSlot) slots.set("ine", reversoSlot);

    if (duplicates.length > 0) {
      problems.push({
        code: "DOCUMENTO_DUPLICADO",
        message: `Más de un archivo para el mismo documento: ${duplicates.join(", ")}.`,
      });
    }

    const missing = REQUIRED_DOC_KINDS.filter((k) => !slots.has(k));
    if (missing.length > 0) {
      problems.push({
        code: "FALTA_DOCUMENTO",
        message: `Falta(n): ${missing.map((k) => DOC_KIND_LABEL[k]).join(", ")}.`,
      });
    }

    const expedienteId = group.name.trim().toUpperCase();
    if (!isValidCurp(expedienteId)) {
      problems.push({
        code: "CURP_INVALIDA",
        message: `El nombre de la carpeta no es una CURP válida: "${group.name}".`,
      });
    }

    const files: BulkFile[] = [];
    let totalBytes = 0;
    const oversizeReported = new Set<File>();
    for (const kind of DOC_KINDS) {
      const slot = slots.get(kind);
      if (!slot) continue;
      const ext = extensionOf(slot.sourceName);
      const contentType = ACCEPTED_EXT_CONTENT_TYPE[ext];
      if (slot.file.size > MAX_FILE_MB * 1024 * 1024) {
        // A duplicated identification shares its File across both slots —
        // report the oversize source once, not once per slot.
        if (!oversizeReported.has(slot.file)) {
          oversizeReported.add(slot.file);
          problems.push({
            code: "ARCHIVO_GRANDE",
            message: `${slot.sourceName} pesa ${(slot.file.size / 1024 / 1024).toFixed(1)} MB (máximo ${MAX_FILE_MB} MB).`,
          });
        }
        continue;
      }
      files.push({ kind, file: slot.file, name: `${kind}${ext}`, contentType });
      totalBytes += slot.file.size;
    }

    expedientes.push({
      folderName: group.name,
      parentPath,
      expedienteId,
      files,
      ignored,
      problems,
      totalBytes,
    });
  }

  // Magic-byte pass over every candidate document (bounded concurrency).
  const checks: { exp: ScannedExpediente; bulkFile: BulkFile }[] = [];
  for (const exp of expedientes) {
    // A duplicated identification (ADR 0007) shares one File across two
    // slots: sniff the physical file once, and report it once if corrupt.
    const seen = new Set<File>();
    for (const bulkFile of exp.files) {
      if (seen.has(bulkFile.file)) continue;
      seen.add(bulkFile.file);
      checks.push({ exp, bulkFile });
    }
  }
  let checked = 0;
  const corrupt = new Map<ScannedExpediente, string[]>();
  const CONCURRENCY = 24;
  let cursor = 0;
  async function checkWorker(): Promise<void> {
    while (cursor < checks.length) {
      const { exp, bulkFile } = checks[cursor++];
      const sniffed = await sniffContentType(bulkFile.file, bulkFile.contentType);
      if (!sniffed) {
        const list = corrupt.get(exp) ?? [];
        list.push(bulkFile.name);
        corrupt.set(exp, list);
      } else if (sniffed !== bulkFile.contentType) {
        // Real content disagrees with the extension-derived guess (e.g. a JPEG
        // named `foto.png`) — trust the content: it's what gets sent as the
        // declared content_type to /uploads/sign and the PUT's Content-Type,
        // and what names the stored object server-side. The correction must
        // reach every slot sharing this File (a duplicated identification is
        // only sniffed once), or the twin's PUT would carry the wrong header.
        for (const twin of exp.files) {
          if (twin.file === bulkFile.file) {
            twin.contentType = sniffed;
            twin.name = `${twin.kind}${CONTENT_TYPE_EXT[sniffed]}`;
          }
        }
      }
      checked++;
      onProgress?.(checked, checks.length);
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, checks.length) }, () => checkWorker()),
  );
  for (const [exp, names] of corrupt) {
    exp.problems.push({
      code: "ARCHIVO_CORRUPTO",
      message: `Archivo vacío, corrupto o de tipo incorrecto: ${names.join(", ")}.`,
    });
  }

  // Duplicate CURPs across folders: the first (by path) keeps the id.
  const byId = new Map<string, ScannedExpediente>();
  for (const exp of [...expedientes].sort((a, b) => a.parentPath.localeCompare(b.parentPath))) {
    const prior = byId.get(exp.expedienteId);
    if (prior && exp.problems.every((p) => p.code !== "CURP_INVALIDA")) {
      exp.problems.push({
        code: "CURP_DUPLICADA",
        message: `La CURP ${exp.expedienteId} ya aparece en la carpeta "${prior.parentPath}".`,
      });
    } else if (!prior) {
      byId.set(exp.expedienteId, exp);
    }
  }

  // Empty directories (drop path): problem rows, outside the CURP-dedup pass —
  // an empty folder must never claim a CURP away from a real expediente.
  const emptyEntries: ScannedExpediente[] = [];
  for (const dirPath of emptyDirPaths) {
    if (isJunkPath(dirPath)) continue;
    const name = dirPath.split("/").filter(Boolean).pop() ?? dirPath;
    const expedienteId = name.trim().toUpperCase();
    const problems: FolderProblem[] = [
      { code: "CARPETA_VACIA", message: "La carpeta no contiene ningún archivo." },
    ];
    if (!isValidCurp(expedienteId)) {
      problems.push({
        code: "CURP_INVALIDA",
        message: `El nombre de la carpeta no es una CURP válida: "${name}".`,
      });
    }
    emptyEntries.push({
      folderName: name,
      parentPath: dirPath,
      expedienteId,
      files: [],
      ignored: [],
      problems,
      totalBytes: 0,
    });
  }

  const ready = expedientes
    .filter((e) => e.problems.length === 0)
    .sort((a, b) => a.expedienteId.localeCompare(b.expedienteId));
  const problematic = [...expedientes.filter((e) => e.problems.length > 0), ...emptyEntries]
    .sort((a, b) => a.folderName.localeCompare(b.folderName));

  return {
    ready,
    problematic,
    looseRootFiles,
    junkSkipped,
    totalReadyFiles: ready.reduce((n, e) => n + e.files.length, 0),
    totalReadyBytes: ready.reduce((n, e) => n + e.totalBytes, 0),
  };
}
